import type { DpiInfo } from '../types/metadata.js';
import type { OfficialBounds } from '../types/profile.js';

/**
 * DPI metadata judgement.
 *
 * This module answers one question only: "what does the file state about its
 * resolution, and does that match a resolution the selected document asks for?".
 * It reports the resolution tag as data and never converts it into any other
 * quantity. A file without DPI metadata is a completely normal digital photo.
 *
 * The official/policy split is respected by construction: only the profile's
 * `official` block can turn a mismatch into a hard failure, and even then only
 * when the profile's own policy marks the violation as an error.
 */

/** Half-width of the band (in DPI) inside which the stated value counts as matching. */
export const DEFAULT_DPI_TOLERANCE = 2;
/** Beyond this deviation the value is reported as "far from the requirement". */
export const DEFAULT_DPI_WARN_TOLERANCE = 30;

export interface DpiRequirement {
  /** Resolution in DPI the document asks for, when it declares one. */
  requiredDpi?: number;
  /** True when the number comes from the profile's `official` block. */
  official: boolean;
}

const isPositive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Reads a DPI bound out of an `official` block.
 *
 * Only an explicit DPI unit/reference counts. A pixel bound whose *statement*
 * happens to mention DPI is not a DPI bound: treating `413 px, 300 dpi` as a
 * requirement of "300" would compare pixels with dots.
 */
export const officialDpiBound = (official: OfficialBounds | undefined): number | undefined => {
  if (!official) return undefined;
  const unit = (official.unit ?? '').toLowerCase();
  const reference = (official.reference ?? '').toLowerCase();
  if (unit !== 'dpi' && !/dpi/.test(reference)) return undefined;
  const value = official.min ?? official.max;
  return isPositive(value) ? value : undefined;
};

/** Resolves the requirement a profile actually states, official first. */
export const dpiRequirementOf = (
  params: Record<string, unknown>,
  official: OfficialBounds | undefined,
): DpiRequirement => {
  const officialValue = officialDpiBound(official);
  const paramValue = params['requiredDpi'];
  if (isPositive(paramValue)) {
    return {
      requiredDpi: paramValue,
      official: officialValue !== undefined && officialValue === paramValue,
    };
  }
  if (officialValue !== undefined) return { requiredDpi: officialValue, official: true };
  return { official: false };
};

/** `300` when both axes agree, `300 × 200` when the file states two values. */
export const formatDpiValue = (dpi: DpiInfo): string =>
  dpi.x === dpi.y ? String(dpi.x) : `${dpi.x} × ${dpi.y}`;

export const dpiSourceLabel = (source: DpiInfo['source']): string => {
  switch (source) {
    case 'exif':
      return 'EXIF';
    case 'jfif':
      return 'JFIF';
    case 'png-phys':
      return 'PNG pHYs';
    default:
      return 'не указан';
  }
};

export type DpiVerdict = 'pass' | 'warning' | 'fail';

export interface DpiAssessment {
  verdict: DpiVerdict;
  requiredDpi?: number;
  /** Absolute deviation of the effective DPI from the requirement. */
  deviation?: number;
  withinTolerance: boolean;
  /** True when the deviation exceeds `warnTolerance`. */
  far: boolean;
  /** True when an official bound plus an `error` policy escalated the verdict to `fail`. */
  escalated: boolean;
}

export interface DpiAssessmentInput {
  dpi: DpiInfo;
  requirement: DpiRequirement;
  tolerance: number;
  warnTolerance: number;
  /** `rule.policy?.onViolation`; only `error` may escalate an official bound. */
  policyOnViolation?: 'warning' | 'error';
}

/**
 * Compares the stated resolution with the document's requirement.
 *
 * Bands: inside ±`tolerance` ⇒ pass; beyond that ⇒ warning; a `fail` is only
 * ever produced when the profile declares an official DPI bound *and* its policy
 * asks for `error` — a bare parameter can never reject a digital photo here.
 */
export const assessDpi = (input: DpiAssessmentInput): DpiAssessment => {
  const { dpi, requirement } = input;
  if (requirement.requiredDpi === undefined) {
    return { verdict: 'pass', withinTolerance: true, far: false, escalated: false };
  }

  // The two axes are stated separately; the effective value is their midpoint.
  const effective = (dpi.x + dpi.y) / 2;
  const deviation = Math.abs(effective - requirement.requiredDpi);
  const withinTolerance = deviation <= input.tolerance;
  const far = deviation > input.warnTolerance;

  if (withinTolerance) {
    return {
      verdict: 'pass',
      requiredDpi: requirement.requiredDpi,
      deviation,
      withinTolerance,
      far,
      escalated: false,
    };
  }

  if (far && requirement.official && input.policyOnViolation === 'error') {
    return {
      verdict: 'fail',
      requiredDpi: requirement.requiredDpi,
      deviation,
      withinTolerance,
      far,
      escalated: true,
    };
  }

  return {
    verdict: 'warning',
    requiredDpi: requirement.requiredDpi,
    deviation,
    withinTolerance,
    far,
    escalated: false,
  };
};
